type AnalyticsEventType = 'page_view' | 'conversion' | 'web_vital' | 'ab_test' | 'error';
type AnalyticsMetadata = Record<string, string | number | boolean | null | undefined>;
type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: Gtag;
  }
}

const SESSION_KEY = 'pdfaa.analytics.session';
const CAMPAIGN_KEY = 'pdfaa.analytics.campaign';
const AB_TEST_KEY = 'pdfaa.ab.launch_page_20260709';
const AB_VARIANTS = ['proof_first', 'savings_first'] as const;
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-KX7987TLB3';
const DEBUG_ANALYTICS = import.meta.env.VITE_ANALYTICS_DEBUG === 'true';
const CAMPAIGN_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
  'coupon',
  'campaign_variant',
  'recipient_id',
  'wave_id',
] as const;

let analyticsInitialised = false;
let webVitalsInitialised = false;

function shouldSkipAnalytics(): boolean {
  if (typeof navigator === 'undefined') return true;
  return /prerenderbot|googlebot|bingbot|cloudflare-ai-search|gptbot|claudebot|perplexitybot/i.test(
    navigator.userAgent,
  );
}

function randomId(): string {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next = randomId();
    sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return 'session-unavailable';
  }
}

function getSearchCampaignMetadata(): AnalyticsMetadata {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const metadata: AnalyticsMetadata = {};

  for (const name of CAMPAIGN_PARAM_NAMES) {
    const value = params.get(name);
    if (value) metadata[name] = value.slice(0, 200);
  }

  return metadata;
}

function getStoredCampaignMetadata(): AnalyticsMetadata {
  try {
    const raw = sessionStorage.getItem(CAMPAIGN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return sanitiseMetadata(parsed as AnalyticsMetadata);
  } catch {
    return {};
  }
}

function rememberCampaignMetadata(metadata: AnalyticsMetadata) {
  if (typeof window === 'undefined') return;
  if (Object.keys(metadata).length === 0) return;

  try {
    const merged = sanitiseMetadata({
      ...getStoredCampaignMetadata(),
      ...metadata,
      landing_path: `${window.location.pathname}${window.location.search}`,
      captured_at: new Date().toISOString(),
    });
    sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify(merged));
  } catch {
    // Campaign attribution must never interrupt the product workflow.
  }
}

function captureCampaignMetadata(): AnalyticsMetadata {
  const metadata = getSearchCampaignMetadata();
  rememberCampaignMetadata(metadata);
  return {
    ...getStoredCampaignMetadata(),
    ...metadata,
  };
}

export function getAbVariant(): string {
  try {
    const existing = localStorage.getItem(AB_TEST_KEY);
    if (existing && AB_VARIANTS.includes(existing as (typeof AB_VARIANTS)[number])) {
      return existing;
    }

    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    const next = AB_VARIANTS[values[0] % AB_VARIANTS.length];
    localStorage.setItem(AB_TEST_KEY, next);
    return next;
  } catch {
    return 'unassigned';
  }
}

function sanitiseMetadata(metadata: AnalyticsMetadata = {}): AnalyticsMetadata {
  const clean: AnalyticsMetadata = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (!/^[a-z0-9_.:-]+$/i.test(key)) continue;
    if (typeof value === 'string') clean[key] = value.slice(0, 500);
    if (typeof value === 'number' || typeof value === 'boolean' || value === null) clean[key] = value;
  }
  return clean;
}

function sendFirstPartyEvent(
  eventName: string,
  eventType: AnalyticsEventType,
  metadata: AnalyticsMetadata = {},
) {
  if (typeof window === 'undefined') return;
  if (shouldSkipAnalytics()) return;

  const payload = {
    event_name: eventName,
    event_type: eventType,
    path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || undefined,
    session_id: getSessionId(),
    ab_variant: getAbVariant(),
    metadata: sanitiseMetadata({
      ...metadata,
      page_title: document.title,
    }),
  };

  const body = JSON.stringify(payload);

  if (DEBUG_ANALYTICS) {
    console.info('[analytics]', payload);
  }

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon('/api/v1/analytics/events', blob);
    return;
  }

  fetch('/api/v1/analytics/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics must never interrupt the product workflow.
  });
}

function sendGoogleEvent(eventName: string, metadata: AnalyticsMetadata = {}) {
  if (!window.gtag) return;
  window.gtag('event', eventName, sanitiseMetadata(metadata));
}

function initGoogleAnalytics() {
  if (!GA_MEASUREMENT_ID || window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

export function trackPageView(path?: string) {
  const currentPath = path || `${window.location.pathname}${window.location.search}`;
  const campaignMetadata = captureCampaignMetadata();
  sendFirstPartyEvent('page_view', 'page_view', { path: currentPath, ...campaignMetadata });

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: currentPath,
      page_title: document.title,
      page_location: window.location.href,
      ...sanitiseMetadata(campaignMetadata),
    });
  }
}

export function trackConversion(eventName: string, metadata: AnalyticsMetadata = {}) {
  const campaignMetadata = captureCampaignMetadata();
  const mergedMetadata = {
    ...campaignMetadata,
    ...metadata,
  };
  sendFirstPartyEvent(eventName, 'conversion', mergedMetadata);
  sendGoogleEvent(eventName, mergedMetadata);
}

export function trackError(eventName: string, metadata: AnalyticsMetadata = {}) {
  sendFirstPartyEvent(eventName, 'error', metadata);
  sendGoogleEvent(eventName, metadata);
}

function trackWebVital(metricName: string, value: number, metadata: AnalyticsMetadata = {}) {
  if (!Number.isFinite(value) || value < 0) return;
  const roundedValue = Math.round(value);
  sendFirstPartyEvent(`web_vital_${metricName.toLowerCase()}`, 'web_vital', {
    metric_name: metricName,
    value: roundedValue,
    ...metadata,
  });
}

function supportsEntryType(type: string): boolean {
  return Boolean(PerformanceObserver.supportedEntryTypes?.includes(type));
}

function initWebVitals() {
  if (webVitalsInitialised || typeof PerformanceObserver === 'undefined') return;
  webVitalsInitialised = true;

  let lcp = 0;
  let cls = 0;
  let inp = 0;

  if (supportsEntryType('paint')) {
    const paintObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          trackWebVital('FCP', entry.startTime);
        }
      }
    });
    paintObserver.observe({ type: 'paint', buffered: true });
  }

  if (supportsEntryType('largest-contentful-paint')) {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) lcp = last.startTime;
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  }

  if (supportsEntryType('layout-shift')) {
    const clsObserver = new PerformanceObserver((list) => {
      for (const rawEntry of list.getEntries()) {
        const entry = rawEntry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!entry.hadRecentInput && typeof entry.value === 'number') {
          cls += entry.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });
  }

  if (supportsEntryType('event')) {
    const inpObserver = new PerformanceObserver((list) => {
      for (const rawEntry of list.getEntries()) {
        const entry = rawEntry as PerformanceEntry & { duration?: number; interactionId?: number };
        if (entry.interactionId && typeof entry.duration === 'number') {
          inp = Math.max(inp, entry.duration);
        }
      }
    });
    inpObserver.observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
  }

  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigation) {
      trackWebVital('TTFB', navigation.responseStart);
    }
  }, { once: true });

  const flushVitals = () => {
    if (lcp) trackWebVital('LCP', lcp);
    trackWebVital('CLS', cls, { value: Math.round(cls * 1000) / 1000 });
    if (inp) trackWebVital('INP', inp);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushVitals();
  });
  window.addEventListener('pagehide', flushVitals);
}

export function initAnalytics() {
  if (analyticsInitialised || typeof window === 'undefined') return;
  analyticsInitialised = true;
  initGoogleAnalytics();
  initWebVitals();
}
